import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Key, Copy, Plus, Trash2, Power, PowerOff, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useApiKeys, CreateApiKeyResponse } from '@/hooks/useApiKeys';

const ApiKeysSettings = () => {
  const { toast } = useToast();
  const { apiKeys, isLoading, fetchApiKeys, createApiKey, deleteApiKey, toggleApiKey } = useApiKeys();
  
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [permissions, setPermissions] = useState({ behavioral: true, cognitive: true });
  const [isCreating, setIsCreating] = useState(false);
  
  // State for showing newly created key
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [showKeyRevealDialog, setShowKeyRevealDialog] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "API key copied to clipboard"
    });
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;
    
    setIsCreating(true);
    const result = await createApiKey(newKeyName, permissions);
    setIsCreating(false);
    
    if (result) {
      setNewlyCreatedKey(result);
      setShowNewKeyDialog(false);
      setShowKeyRevealDialog(true);
      setNewKeyName('');
      setPermissions({ behavioral: true, cognitive: true });
    }
  };

  const handleKeyRevealClose = () => {
    setShowKeyRevealDialog(false);
    setNewlyCreatedKey(null);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const baseUrl = 'https://kenlllgwhwjnfxkynwem.supabase.co/functions/v1/external-api';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">API Keys</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage API keys for external services to access your assessment data
        </p>
      </div>

      {/* API Documentation Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">API Documentation</CardTitle>
          <CardDescription>
            Use these endpoints to integrate with external services like OpenClaw, ATS systems, etc.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Base URL</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-muted px-3 py-2 rounded font-mono">
                {baseUrl}
              </code>
              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(baseUrl)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Example Request</Label>
            <pre className="text-xs bg-muted px-3 py-2 rounded font-mono overflow-x-auto">
{`curl -H "Authorization: Bearer sk_live_your_api_key" \\
  ${baseUrl}/candidates`}
            </pre>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-1">
              <p className="font-medium">Available Endpoints:</p>
              <ul className="text-muted-foreground space-y-1">
                <li><code className="text-xs">/candidates</code> - List all candidates</li>
                <li><code className="text-xs">/candidates/:id</code> - Get candidate details</li>
                <li><code className="text-xs">/behavioral/:id</code> - Get behavioral profile</li>
                <li><code className="text-xs">/cognitive/:id</code> - Get cognitive results</li>
              </ul>
            </div>
            <div className="space-y-1">
              <p className="font-medium">Query Parameters:</p>
              <ul className="text-muted-foreground space-y-1">
                <li><code className="text-xs">limit</code> - Max results (default 50, max 200)</li>
                <li><code className="text-xs">offset</code> - Pagination offset</li>
                <li><code className="text-xs">since</code> - Filter by date (ISO format)</li>
                <li><code className="text-xs">email</code> - Filter by email</li>
              </ul>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <span>Rate limit: 100 requests per hour</span>
          </div>
        </CardContent>
      </Card>

      {/* API Keys List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Key className="h-5 w-5" />
                Your API Keys
              </CardTitle>
              <CardDescription>
                Create and manage API keys for your applications
              </CardDescription>
            </div>
            {apiKeys.length > 0 && (
              <Button onClick={() => setShowNewKeyDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Key
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12">
              <Key className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium mb-2">No API Keys</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first API key to allow external services to access your data
              </p>
              <Button onClick={() => setShowNewKeyDialog(true)}>
                Create API Key
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((apiKey) => (
                <div key={apiKey.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{apiKey.name}</p>
                      <Badge variant={apiKey.is_active ? "default" : "secondary"}>
                        {apiKey.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                        {apiKey.key_prefix}
                      </code>
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                      <span>Created: {formatDate(apiKey.created_at)}</span>
                      <span>Last used: {formatDate(apiKey.last_used_at)}</span>
                      <span>Requests: {apiKey.request_count}</span>
                    </div>
                    <div className="flex gap-2">
                      {apiKey.permissions.behavioral && (
                        <Badge variant="outline" className="text-xs">Behavioral</Badge>
                      )}
                      {apiKey.permissions.cognitive && (
                        <Badge variant="outline" className="text-xs">Cognitive</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleApiKey(apiKey.id, !apiKey.is_active)}
                      title={apiKey.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {apiKey.is_active ? (
                        <PowerOff className="h-4 w-4" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete API Key?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete this API key. Any applications using this key will stop working immediately.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteApiKey(apiKey.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={showNewKeyDialog} onOpenChange={setShowNewKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New API Key</DialogTitle>
            <DialogDescription>
              Give your API key a descriptive name and select the data it can access
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key-name">API Key Name</Label>
              <Input
                id="key-name"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., OpenClaw Integration, ATS Connection"
              />
            </div>
            <div className="space-y-3">
              <Label>Permissions</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="perm-behavioral"
                  checked={permissions.behavioral}
                  onCheckedChange={(checked) => 
                    setPermissions(prev => ({ ...prev, behavioral: !!checked }))
                  }
                />
                <Label htmlFor="perm-behavioral" className="text-sm font-normal cursor-pointer">
                  Behavioral data (DISC profiles, scores, insights)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="perm-cognitive"
                  checked={permissions.cognitive}
                  onCheckedChange={(checked) => 
                    setPermissions(prev => ({ ...prev, cognitive: !!checked }))
                  }
                />
                <Label htmlFor="perm-cognitive" className="text-sm font-normal cursor-pointer">
                  Cognitive data (percentiles, verbal/numerical/abstract scores)
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewKeyDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateKey} 
              disabled={!newKeyName.trim() || isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Key'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Key Reveal Dialog - Only shown once after creation */}
      <Dialog open={showKeyRevealDialog} onOpenChange={handleKeyRevealClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Copy Your API Key Now
            </DialogTitle>
            <DialogDescription>
              This is the only time you'll see this key. Copy it now and store it securely.
            </DialogDescription>
          </DialogHeader>
          {newlyCreatedKey && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-muted px-3 py-2 rounded font-mono break-all">
                    {newlyCreatedKey.key}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(newlyCreatedKey.key)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                <p className="text-sm text-destructive">
                  <strong>Warning:</strong> You won't be able to see this key again. If you lose it, you'll need to create a new one.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={handleKeyRevealClose}>
              I've Copied the Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiKeysSettings;
